import { Board } from "../board";
import { XY } from "../xy";
import { MusicId } from "../musicId.ts";
import { BOARD_HEIGHT, BOARD_WIDTH } from "../gameConfig.ts";
import { assignGoldToPrePlacedMobs, populateCellsWithMobs } from "./levelGeneratorUtils.ts";
import { buildPrefab } from "./prefabs/prefabBuilder.ts";
import type { PrefabData } from "./prefabs/prefabData.ts";
import { GrayStoneFloor1 } from "../things/floors/grayStoneFloor1.ts";
import { GrayStoneFloor2 } from "../things/floors/grayStoneFloor2.ts";
import { MetalBlock } from "../things/metalBlock.ts";
import { ConcreteBlock } from "../things/concreteBlock.ts";
import { CaveRobot } from "../mobs/caveRobot.ts";
import { CaveFloor } from "../things/floors/caveFloor.ts";
import { GraySlab } from "../things/floors/graySlab.ts";
import { DarkConcreteFloor } from "../things/floors/darkConcreteFloor.ts";
import { Fence } from "../things/fence.ts";
import { SlidingDoor } from "../things/slidingDoor.ts";
import { Direction } from "../direction.ts";
import { WoodenDoor } from "../things/woodenDoor.ts";
import { WoodenFloor } from "../things/floors/woodenFloor.ts";
import { ConcreteFloor } from "../things/floors/concreteFloor.ts";
import {MapMarker} from "../things/mapMarker.ts";
import {DebugIndicator} from "../things/debugIndicator.ts";
import {AlbinoSquid} from "../mobs/albinoSquid.ts";
import {Squid} from "../mobs/squid.ts";
import { Horned } from "../mobs/horned.ts";
import { Slugant } from "../mobs/slugant.ts";
import { ToxicSlug } from "../mobs/toxicSlug.ts";
import { Biter } from "../mobs/biter.ts";
import { BigSquid } from "../mobs/bigSquid.ts";
import {Upstairs} from "../things/upstairs.ts";
import {Downstairs} from "../things/downstairs.ts";
import {FenceLight} from "../things/fenceLight.ts";
import {SlidingGate} from "../things/slidingGate.ts";
import {Terminal} from "../things/terminal.ts";
import {OpenBlastDoorsTerminal} from "../things/openBlastDoorsTerminal.ts";
import {
	ZOO_CELL_DOOR_GROUP,
	ZOO_ENTRANCE_DOOR_GROUP,
	ZOO_EXIT_DOOR_GROUP,
	ZooReleaseTerminal,
} from "../things/zooReleaseTerminal.ts";
import { Player } from "../player.ts";

/**
 * Whole-board scratch prefab (50x28 — must match BOARD_WIDTH/BOARD_HEIGHT), for
 * handcrafting a full level like wastelandLevel4PrefabData: layer 1 is floors,
 * layer 2 is structures/things. Edit the legend + layers below and reload the
 * playground to see it. The corner MetalBlocks are just reference markers.
 */
const scratchPrefabData: PrefabData = {
	legend: {
		"@": Player.key,
		"[": ConcreteBlock.key,
		"#": MetalBlock.key,
		"%": FenceLight.key,
		"&": ConcreteBlock.key, // inner walls
		".": DarkConcreteFloor.key,
		"+": WoodenDoor.key,
		1: { class: SlidingDoor.key, props: { direction: Direction.N, group: ZOO_ENTRANCE_DOOR_GROUP } },
		2: { class: SlidingDoor.key, props: { direction: Direction.S, group: ZOO_ENTRANCE_DOOR_GROUP } },
		3: { class: SlidingDoor.key, props: { direction: Direction.N, group: ZOO_EXIT_DOOR_GROUP } },
		">": { class: SlidingGate.key, props: { direction: Direction.W, group: ZOO_CELL_DOOR_GROUP } },
		"<": { class: SlidingGate.key, props: { direction: Direction.E, group: ZOO_CELL_DOOR_GROUP } },
		"V": { class: SlidingGate.key, props: { direction: Direction.N, group: ZOO_CELL_DOOR_GROUP } },
		"A": { class: SlidingGate.key, props: { direction: Direction.S, group: ZOO_CELL_DOOR_GROUP } },
		"S": { class: MapMarker.key, props: { tag: "cell" } },
		"U": { class: Upstairs.key},
		"D": { class: Downstairs.key},
		"T": { class: ZooReleaseTerminal.key},
		// "S": { class: Squid.key, props: { tag: "cell" } },
	},
	layers: [
		[
			"                                                  ",
			"..................................................",
			"..................................................",
			"..................................................",
			"..................................................",
			"......                                      ......",
			"......                                      ......",
			"......                                      ......",
			"......                                      ......",
			"......                                            ",
			"......     ............................           ",
			"......     ............................           ",
			"......     ............................           ",
			"......     ............................     ......",
			"......     ............................     ......",
			"           ............................     ......",
			"           ............................     ......",
			"           ............................     ......",
			"                                            ......",
			"......                                      ......",
			"......                                      ......",
			"......                                      ......",
			"......                                      ......",
			"..................................................",
			"..................................................",
			"..................................................",
			"..................................................",
			"                                                  ",
		],
		[
			"[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[",
			"[      &    &    &    &     &    &    &   &      [",
			"[  S   & S  & S  & S  &  S  & S  & S  & S &  S   [",
			"[      &    &    &    &     &    &    &   &      [",
			"[&&&&%>%%><%%%><%%%><%%%><%%%%><%%%><%%%>%&<&&&&&[",
			"[    %                                      %    [",
			"[    %                                      V S  [",
			"[ S  V                                      %    [",
			"[    %                                      ######",
			"[&&&&%                                      #  T [",
			"[    %     %%%><%%%><%%%><%%%><%%%><%%%     1    [",
			"[    %     %     &    &    &    &     %     2  @ [",
			"[ S  V     A  S  & S  & S  & S  & S   A     #  U [",
			"[    %     %     &    &    &    &     %     ######",
			"######     %&&&&&&&&&&&&&&&&&&&&&&&&&&%     %    [",
			"[    #     %  S  & S  & S  & S  & S   %     %    [",
			"[ D  #     %     &    &    &    &     %     V  S [",
			"[    3     %%%><%%%><%%%><%%%><%%%><%%%     %    [",
			"[    #                                      %    [",
			"######                                      %&&&&[",
			"[    %                                      %    [",
			"[  S V                                      V  S [",
			"[    %                                      %    [",
			"[%%%%%>%%><%%%><%%%><%%%><%%%%><%%%><%%%>%&<&&&&&[",
			"[      &    &    &    &     &    &    &   &      [",
			"[   S  & S  & S  & S  &  S  & S  & S  & S &  S   [",
			"[      &    &    &    &     &    &    &   &      [",
			"[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[[",
		],
	],
};

/**
 * Dev-only level generator. Starts as a nearly-empty board — drop in whatever
 * prefabs, mobs, or entities you want to poke at for the current experiment.
 */
export class PlaygroundLevelGenerator {
	generateNewLevel(levelNumber: number): Board {
		const board = new Board(BOARD_WIDTH, BOARD_HEIGHT);
		board.defaultFloor = ConcreteFloor.key;

		const scratch = buildPrefab(scratchPrefabData);
		const scratchOrigin = new XY(
			Math.floor((BOARD_WIDTH - scratch.simpleBoard.getWidth()) / 2),
			Math.floor((BOARD_HEIGHT - scratch.simpleBoard.getHeight()) / 2),
		);
		scratch.addToBoard(board, scratchOrigin);

		populateCellsWithMobs(board, [Squid, AlbinoSquid, Horned, Slugant, ToxicSlug, Biter, BigSquid]);

		assignGoldToPrePlacedMobs(board);

		board.areaName = `Playground ${levelNumber}`;
		board.initialSpawnXY = new XY(5, 5);
		board.musicId = MusicId.CAVES;

		// Dev/QA effect-visualizer fixture. Edit EffectTester.processTurn to inspect
		// whichever effect is currently under development.
		// const effectTesterXY = findEmptyPassableNear(board, new XY(10, 10));
		// if (effectTesterXY) board.addThing(new EffectTester(effectTesterXY));

		return board;
	}
}

function findEmptyPassableNear(board: Board, anchor: XY): XY | null {
	const queue: XY[] = [anchor];
	const visited = new Set<string>();
	while (queue.length > 0) {
		const xy = queue.shift()!;
		const key = xy.toString();
		if (visited.has(key)) continue;
		visited.add(key);
		if (visited.size > 400) return null;
		if (!board.isInBounds(xy)) continue;
		if (board.isPassable(xy) && !board.hasMob(xy)) return xy;
		for (const n of xy.getNeighbors()) queue.push(n);
	}
	return null;
}
